'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { authApi } from '@/lib/api/auth';
import { termsApi } from '@/lib/api/terms';
import { useAuthStore } from '@/stores/auth-store';
import { getErrorMessage } from '@/lib/errors';
import { CURRENT_TERMS_VERSION } from '@/lib/terms-content';

/**
 * Страница регистрации
 */
export default function RegisterPage() {
  const router = useRouter();
  const { setAuth } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [consent, setConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirm: '',
  });
  const [errors, setErrors] = useState<{ email?: string; password?: string; password_confirm?: string; consent?: string; terms?: string }>({});

  /**
   * Валидация email
   */
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Валидация формы
   */
  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string; password_confirm?: string; consent?: string; terms?: string } = {};

    if (!formData.email) {
      newErrors.email = 'Email обязателен';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Некорректный формат email';
    }

    if (!formData.password) {
      newErrors.password = 'Пароль обязателен';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Пароль должен быть не менее 6 символов';
    }

    if (!formData.password_confirm) {
      newErrors.password_confirm = 'Подтвердите пароль';
    } else if (formData.password !== formData.password_confirm) {
      newErrors.password_confirm = 'Пароли не совпадают';
    }

    if (!consent) {
      newErrors.consent = 'Необходимо согласие с политикой обработки персональных данных';
    }

    if (!termsAccepted) {
      newErrors.terms = 'Необходимо принять Пользовательское соглашение';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Обработка изменения поля с очисткой ошибки
   */
  const handleFieldChange = (field: 'email' | 'password' | 'password_confirm', value: string) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: undefined });
    }
  };

  /**
   * Обработка отправки формы
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await authApi.register({
        email: formData.email,
        password: formData.password,
        consent,
      });
      setAuth(response.user, response.token.access_token, response.token.refresh_token, true);

      // Фиксация акцепта Пользовательского соглашения (оферты, SUP-107).
      // Best-effort: ошибка не блокирует регистрацию — акцепт можно повторить через баннер.
      try {
        await termsApi.accept(CURRENT_TERMS_VERSION);
      } catch {
        // Не логируем как ошибку — пользователь увидит баннер при следующем входе.
      }

      toast.success('Регистрация успешна! Добро пожаловать!');
      router.push('/dashboard');
    } catch (err: unknown) {
      toast.error(getErrorMessage(err, 'Ошибка регистрации'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="sr-only">Регистрация аккаунта</h1>
      <div className="auth-card">
        <CardHeader className="text-center space-y-3 p-0">
          <CardTitle className="text-xl font-bold text-foreground">Регистрация</CardTitle>
        </CardHeader>
        <CardContent className="p-0 pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              error={errors.email}
              autoComplete="email"
            />

            <div className="space-y-1">
              <label htmlFor="register-password" className="text-sm font-medium text-foreground">Пароль</label>
              <div className="relative flex items-center">
                <input
                  id="register-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Минимум 6 символов…"
                  value={formData.password}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                  autoComplete="new-password"
                  spellCheck={false}
                  className={`flex h-10 w-full rounded-md border bg-input px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${
                    errors.password ? 'border-destructive' : 'border-border'
                  }`}
                />
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setShowPassword(!showPassword);
                  }}
                  className="absolute right-3 z-20 p-2 text-muted-foreground hover:text-foreground cursor-pointer"
                  style={{ touchAction: 'manipulation' }}
                  aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
            </div>

            <div className="space-y-1">
              <label htmlFor="register-password-confirm" className="text-sm font-medium text-foreground">Подтверждение пароля</label>
              <div className="relative flex items-center">
                <input
                  id="register-password-confirm"
                  type={showPasswordConfirm ? 'text' : 'password'}
                  placeholder="Повторите пароль…"
                  value={formData.password_confirm}
                  onChange={(e) => handleFieldChange('password_confirm', e.target.value)}
                  autoComplete="new-password"
                  spellCheck={false}
                  className={`flex h-10 w-full rounded-md border bg-input px-3 py-2 pr-10 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${
                    errors.password_confirm ? 'border-destructive' : 'border-border'
                  }`}
                />
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setShowPasswordConfirm(!showPasswordConfirm);
                  }}
                  className="absolute right-3 z-20 p-2 text-muted-foreground hover:text-foreground cursor-pointer"
                  style={{ touchAction: 'manipulation' }}
                  aria-label={showPasswordConfirm ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showPasswordConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password_confirm && <p className="text-sm text-destructive">{errors.password_confirm}</p>}
            </div>

            <div className="flex items-start space-x-2 pt-1">
              <Checkbox
                id="consent"
                checked={consent}
                onCheckedChange={(checked) => {
                  setConsent(checked === true);
                  if (errors.consent) {
                    setErrors({ ...errors, consent: undefined });
                  }
                }}
                aria-required="true"
                aria-invalid={!!errors.consent}
              />
              <div className="space-y-1">
                <label htmlFor="consent" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                  Я согласен с{' '}
                  <Link href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    политикой обработки персональных данных
                  </Link>
                </label>
                {errors.consent && <p className="text-sm text-destructive">{errors.consent}</p>}
              </div>
            </div>

            <div className="flex items-start space-x-2 pt-1">
              <Checkbox
                id="terms"
                checked={termsAccepted}
                onCheckedChange={(checked) => {
                  setTermsAccepted(checked === true);
                  if (errors.terms) {
                    setErrors({ ...errors, terms: undefined });
                  }
                }}
                aria-required="true"
                aria-invalid={!!errors.terms}
              />
              <div className="space-y-1">
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed cursor-pointer">
                  Я принимаю{' '}
                  <Link href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                    Пользовательское соглашение
                  </Link>
                </label>
                {errors.terms && <p className="text-sm text-destructive">{errors.terms}</p>}
              </div>
            </div>

            <Button type="submit" className="w-full" disabled={loading || !consent || !termsAccepted}>
              {loading ? 'Регистрация…' : 'Зарегистрироваться'}
            </Button>

            <p className="pt-2 text-center text-sm text-muted-foreground">
              Уже есть аккаунт?{' '}
              <Link href="/login" className="font-medium text-primary hover:underline">
                Войти
              </Link>
            </p>
          </form>
        </CardContent>
      </div>
    </>
  );
}
